import { z } from 'zod'

/**
 * Common Zod validators for reuse across resource schemas.
 * These match typical Rails model validations.
 */

const blankToUndefined = (val) =>
  (val === '' || val === null) ? undefined : val

// String validators
export const requiredString = (fieldName = 'This field') =>
  z.string().min(1, `${fieldName} is required`)

export const optionalString = z.string().optional().or(z.literal(''))

export const optionalStringWithMax = (max, fieldName = 'This field') =>
  z.string().max(max, `${fieldName} must be ${max} characters or less`).optional().or(z.literal(''))

// URL validator - allows empty string or valid URL
export const optionalUrl = z.string().url('Must be a valid URL').optional().or(z.literal(''))

export const requiredUrl = (fieldName = 'URL') =>
  z.string().url(`${fieldName} must be a valid URL`).min(1, `${fieldName} is required`)

// Email validator - allows empty string or valid email
export const optionalEmail = z.string().email('Must be a valid email').optional().or(z.literal(''))

export const requiredEmail = (fieldName = 'Email') =>
  z.string().email(`${fieldName} must be valid`).min(1, `${fieldName} is required`)

// Number validators
export const requiredPositiveNumber = (fieldName = 'This field') =>
  z.coerce.number().min(0, `${fieldName} must be positive`)

export const requiredPositiveInteger = (fieldName = 'This field') =>
  z.coerce.number().int().min(1, `${fieldName} must be at least 1`)

export const optionalPositiveNumber = z.coerce.number().min(0).optional().or(z.literal(''))

export const optionalNumber = z.coerce.number().optional().or(z.literal(''))

// Optional integer with a minimum (allows blank)
export const optionalIntegerMin = (min, fieldName = 'This field') =>
  z.preprocess(
    blankToUndefined,
    z.coerce.number().int().min(min, `${fieldName} must be at least ${min}`).optional()
  )

// Optional non-negative number (allows blank, rejects negatives)
export const optionalNonNegativeNumber = (fieldName = 'This field') =>
  z.preprocess(
    blankToUndefined,
    z.coerce.number().min(0, `${fieldName} cannot be negative`).optional()
  )

// Required, non-negative integer pence (>= 0).
// Use for required currency fields where 0 is acceptable.
export const currencyPence = (fieldName = 'Price') =>
  z.preprocess(
    blankToUndefined,
    z.coerce.number({
      required_error: `${fieldName} is required`,
      invalid_type_error: `${fieldName} is required`
    })
      .int()
      .min(0, `${fieldName} cannot be negative`)
  )

// Required, must be greater than 0 integer pence.
export const positiveCurrencyPence = (fieldName = 'Price') =>
  z.preprocess(
    blankToUndefined,
    z.coerce.number({
      required_error: `${fieldName} is required`,
      invalid_type_error: `${fieldName} is required`
    })
      .int()
      .positive(`${fieldName} must be greater than 0`)
  )

// Optional, non-negative integer pence (>= 0 or blank).
export const optionalCurrencyPence = (fieldName = 'Amount') =>
  z.preprocess(
    blankToUndefined,
    z.coerce.number().int().min(0, `${fieldName} cannot be negative`).optional()
  )

// Select/enum validator - must be non-empty
export const requiredSelect = (fieldName = 'This field') =>
  z.string().min(1, `${fieldName} is required`)

// Boolean with default
export const optionalBoolean = z.boolean().optional().default(true)

// File validators - for optional file uploads
export const optionalFile = z.any().optional().nullable()

// Phone number - basic validation
export const optionalPhone = z.string()
  .regex(/^[\d\s+()-]*$/, 'Must be a valid phone number')
  .optional()
  .or(z.literal(''))

/**
 * Helper to create a schema with common patterns
 */
export function createResourceSchema(fields) {
  return z.object(fields)
}
